import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Mail, Linkedin, Send } from "lucide-react";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [userType, setUserType] = useState(""); // For better targeting
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Validation logic for form fields
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFormValid = useMemo(() => {
    return (
      formData.name.trim().length > 0 &&
      formData.email.trim().length > 0 &&
      isValidEmail(formData.email) &&
      formData.message.trim().length > 10 // Minimum message length
    );
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid) {
      toast({
        title: "Please complete all required fields",
        description: "Make sure all fields are filled out correctly before submitting.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare form data for POST request
      const submissionData = {
        name: formData.name,
        email: formData.email,
        userType: userType || 'Not specified',
        message: formData.message
      };

      // TODO: Replace with your actual endpoint URL
      const response = await fetch('https://api.airtap.co.za', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      toast({
        title: "Message Sent Successfully!",
        description: `Thanks ${formData.name}! Your message has been sent directly to our team. We'll respond within 24 hours.`,
      });

      // Reset form
      setFormData({ name: "", email: "", message: "" });
      setUserType("");
      
    } catch (error) {
      console.error('Contact form submission failed:', error);
      
      // Fallback to mailto on error
      const fallbackMessage = `
        Contact Form Submission from TAP Website

        Name: ${formData.name}
        Email: ${formData.email}
        User Type: ${userType || 'Not specified'}

        Message:
        ${formData.message}

        ---
        This message was sent from the TAP website contact form.
      `.trim();
      
      const subject = encodeURIComponent(`Contact Form - ${userType || 'General'} Inquiry`);
      const body = encodeURIComponent(fallbackMessage);
      const mailtoUrl = `mailto:info@airtalent.co.za?subject=${subject}&body=${body}`;
      
      toast({
        title: "Sending via Email Client",
        description: "Service is temporarily unavailable. Opening your email client as a backup method.",
        variant: "default"
      });
      
      window.open(mailtoUrl, '_blank');
      
      // Reset form on fallback as well
      setFormData({ name: "", email: "", message: "" });
      setUserType("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="page-gradient relative pt-12 min-[375px]:pt-14 min-[425px]:pt-16 sm:pt-20 lg:pt-28 pb-12 min-[375px]:pb-14 min-[425px]:pb-16 sm:pb-20 lg:pb-24" data-testid="section-contact">
      <div className="flex flex-col w-full max-w-7xl mx-auto px-4 min-[375px]:px-6 min-[425px]:px-8 sm:px-6 lg:px-10">
        <div className="text-center lg:text-left mb-8 min-[375px]:mb-10 min-[425px]:mb-12 lg:mb-20">
          <h2 className="text-2xl min-[375px]:text-3xl min-[425px]:text-4xl sm:text-4xl md:text-5xl lg:text-[60px] font-bold text-[#2D2D2D] font-space-grotesk leading-[130%] mb-3 sm:mb-4 lg:mb-6" data-testid="text-contact-title">
            CONTACT US
          </h2>

        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-16 xl:gap-20 items-start">
          {/* Contact Form */}
          <div className="order-1 border border-[#EAEAEA] rounded-[10px] p-4 sm:p-6 md:p-8 lg:p-8 xl:p-10 bg-white shadow-card">
            <h3 className="text-lg min-[375px]:text-xl min-[425px]:text-2xl lg:text-2xl font-bold text-[#2D2D2D] font-space-grotesk leading-[130%] mb-4 sm:mb-6">Get In Touch</h3>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6" data-testid="form-contact">
              {/* Professional user type selection */}
              <div className="grid grid-cols-2 gap-2 xs:gap-3 sm:gap-4 mb-4 sm:mb-6">
                <button
                  type="button"
                  onClick={() => setUserType('referrer')}
                  className={`p-2.5 xs:p-3 sm:p-4 rounded-[10px] border transition-all duration-300 text-xs xs:text-sm sm:text-sm font-semibold font-space-grotesk min-h-[40px] xs:min-h-[44px] ${
                    userType === 'referrer' 
                      ? 'border-[#FF7A00] bg-[#FF7A00] text-white' 
                      : 'border-[#EAEAEA] text-[#2D2D2D] hover:border-[#FF7A00] hover:bg-[#FF7A00]/10'
                  }`}
                  data-testid="button-user-type-referrer"
                >
                  Referrer
                </button>
                <button
                  type="button"
                  onClick={() => setUserType('employer')}
                  className={`p-2.5 xs:p-3 sm:p-4 rounded-[10px] border transition-all duration-300 text-xs xs:text-sm sm:text-sm font-semibold font-space-grotesk min-h-[40px] xs:min-h-[44px] ${
                    userType === 'employer' 
                      ? 'border-[#FF7A00] bg-[#FF7A00] text-white' 
                      : 'border-[#EAEAEA] text-[#2D2D2D] hover:border-[#FF7A00] hover:bg-[#FF7A00]/10'
                  }`}
                  data-testid="button-user-type-employer"
                >
                  Employer
                </button>
              </div>
              
              {/* Simplified 3-field form */}
              <div className="space-y-3 sm:space-y-4 md:space-y-5">
                <div className="relative">
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Full Name *"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full h-10 xs:h-11 sm:h-12 bg-white/60 backdrop-blur-sm border rounded-[10px] text-[#2D2D2D] placeholder:text-[#2D2D2D]/60 outline-none focus:outline-none focus-visible:outline-none focus:ring-orange-200 px-3 xs:px-3 sm:px-4 text-sm sm:text-base font-manrope transition-all duration-300 ${
                      formData.name.trim() 
                        ? 'border-orange-300 focus:border-orange-300' 
                        : 'border-slate-200 focus:border-orange-300'
                    }`}
                    data-testid="input-contact-name"
                    autoComplete="name"
                  />
                  {formData.name.trim() && (
                    <div className="absolute right-2.5 xs:right-3 top-1/2 transform -translate-y-1/2">
                      <div className="w-1.5 xs:w-2 h-1.5 xs:h-2 bg-[#FF7A00] rounded-full"></div>
                    </div>
                  )}
                </div>
                
                <div className="relative">
                  <Input
                    type="email"
                    inputMode="email"
                    name="email"
                    placeholder="Your Email Address *"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full h-10 xs:h-11 sm:h-12 bg-white/60 backdrop-blur-sm border rounded-[10px] text-[#2D2D2D] placeholder:text-[#2D2D2D]/60 outline-none focus:outline-none focus-visible:outline-none focus:ring-orange-200 px-3 xs:px-3 sm:px-4 text-sm sm:text-base font-manrope transition-all duration-300 ${
                      formData.email.trim() && isValidEmail(formData.email)
                        ? 'border-orange-300 focus:border-orange-300' 
                        : formData.email.trim() && !isValidEmail(formData.email)
                        ? 'border-red-400 focus:border-red-400'
                        : 'border-slate-200 focus:border-orange-300'
                    }`}
                    data-testid="input-contact-email"
                    autoComplete="email"
                  />
                  {formData.email.trim() && (
                    <div className="absolute right-2.5 xs:right-3 top-1/2 transform -translate-y-1/2">
                      <div className={`w-1.5 xs:w-2 h-1.5 xs:h-2 rounded-full ${
                        isValidEmail(formData.email) ? 'bg-[#FF7A00]' : 'bg-red-400'
                      }`}></div>
                    </div>
                  )}
                </div>
                
                <div className="relative">
                  <Textarea
                    name="message"
                    placeholder={userType === 'referrer' ? "Tell us about your network, referral interests, and how you'd like to get involved... (minimum 10 characters) *" : userType === 'employer' ? "Describe your hiring needs, role requirements, and timeline... (minimum 10 characters) *" : "How can we help you today? (minimum 10 characters) *"}
                    rows={3}
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full bg-white/60 backdrop-blur-sm border rounded-[10px] text-[#2D2D2D] placeholder:text-[#2D2D2D]/60 outline-none focus:outline-none focus-visible:outline-none focus:ring-orange-200 px-3 xs:px-3 sm:px-4 py-2.5 xs:py-2.5 sm:py-3 text-sm sm:text-base font-manrope resize-none transition-all duration-300 ${
                      formData.message.trim().length >= 10
                        ? 'border-orange-300 focus:border-orange-300' 
                        : formData.message.trim().length > 0
                        ? 'border-yellow-400 focus:border-yellow-400'
                        : 'border-slate-200 focus:border-orange-300'
                    }`}
                    data-testid="textarea-contact-message"
                  />
                  {formData.message.trim() && (
                    <div className="absolute right-2.5 xs:right-3 top-2.5 xs:top-3">
                      <div className={`w-1.5 xs:w-2 h-1.5 xs:h-2 rounded-full ${
                        formData.message.trim().length >= 10 ? 'bg-[#FF7A00]' : 'bg-yellow-400'
                      }`}></div>
                    </div>
                  )}
                  <div className="text-right text-xs text-[#2D2D2D]/60 font-manrope mt-1">
                    {formData.message.trim().length}/10 minimum
                  </div>
                </div>
              </div>
              
              <Button 
                type="submit" 
                disabled={!isFormValid || isSubmitting}
                className={`w-full h-11 xs:h-12 sm:h-14 rounded-[10px] font-bold text-sm xs:text-base sm:text-lg font-space-grotesk transition-all duration-300 shadow-card flex items-center justify-center gap-1.5 xs:gap-2 min-h-[44px] xs:min-h-[48px] ${
                  isFormValid && !isSubmitting
                    ? 'bg-[#FF7A00] text-white hover:bg-[#FF7A00]/90' 
                    : 'bg-[#EAEAEA] text-[#2D2D2D]/60 cursor-not-allowed'
                }`}
                data-testid="button-contact-submit"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 xs:w-5 h-4 xs:h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-xs xs:text-sm sm:text-base">Processing...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 xs:w-5 h-4 xs:h-5" />
                    <span className="text-xs xs:text-sm sm:text-base">Send Message</span>
                  </>
                )}
              </Button>
              
              {!isFormValid && (
                <div className="text-center text-[#2D2D2D]/60 text-xs xs:text-sm font-manrope mt-2">
                  Please fill out all required fields correctly to send your message
                </div>
              )}
            </form>
          </div>
          
          {/* Contact Information */}
          <div className="order-2 space-y-4 sm:space-y-6 md:space-y-8">
            <h3 className="text-lg min-[375px]:text-xl min-[425px]:text-2xl lg:text-2xl font-bold text-[#2D2D2D] font-space-grotesk leading-[130%] mb-4 sm:mb-6">Contact Information</h3>
            
            <div className="space-y-3 xs:space-y-4 sm:space-y-6">
              {[
                { 
                  icon: Mail, 
                  label: "Email", 
                  value: "contact@airtalent.co.uk",
                  href: "mailto:contact@airtalent.co.uk",
                  testId: "email" 
                },
                { 
                  icon: Linkedin, 
                  label: "LinkedIn", 
                  value: "Follow us on LinkedIn",
                  href: "https://www.linkedin.com/showcase/air-tap",
                  testId: "linkedin" 
                }
              ].map((contact, index) => {
                const IconComponent = contact.icon;
                return (
                  <a
                    key={index}
                    href={contact.href}
                    className="group flex items-center space-x-3 xs:space-x-4 sm:space-x-6 p-3 xs:p-4 sm:p-6 border border-[#EAEAEA] rounded-[10px] bg-white hover:border-[#FF7A00] hover:bg-[#FF7A00]/5 transition-all duration-300 shadow-card"
                    data-testid={`link-contact-${contact.testId}`}
                    target={contact.testId === 'linkedin' ? '_blank' : undefined}
                    rel={contact.testId === 'linkedin' ? 'noopener noreferrer' : undefined}
                  >
                    <div className="w-9 h-9 xs:w-10 xs:h-10 sm:w-12 sm:h-12 bg-[#FF7A00]/10 rounded-[10px] flex items-center justify-center group-hover:bg-[#FF7A00]/20 transition-all duration-300 flex-shrink-0">
                      <IconComponent className="w-5 h-5 xs:w-6 xs:h-6 text-[#FF7A00]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[#2D2D2D]/80 text-xs xs:text-sm font-medium font-manrope">{contact.label}</div>
                      <div className="text-[#2D2D2D] text-sm xs:text-base sm:text-lg font-semibold font-space-grotesk group-hover:text-[#FF7A00] transition-colors duration-300 break-words">
                        {contact.value}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
